import React, { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import type { CountryOption } from '../types';
import { CheckIcon, XMarkIcon as XIcon, PlusIcon, MagnifyingGlassIcon as SearchIcon, ChevronDownIcon, ChevronRightIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogBackdrop, DialogPanel, Transition } from '@headlessui/react';
import { Operator, GtPrefix } from '../types';

const Operators: React.FC = () => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [gtPrefixes, setGtPrefixes] = useState<Record<number, GtPrefix[]>>({});
  const [loadingPrefixes, setLoadingPrefixes] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState({
    country: '',
    agreement_type: '',
    is_allowed: '',
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<any>(null);
  const [isGtPrefixModalOpen, setIsGtPrefixModalOpen] = useState(false);
  const [editingGtPrefix, setEditingGtPrefix] = useState<GtPrefix | null>(null);
  const [currentOperatorId, setCurrentOperatorId] = useState<number | null>(null);
  const [gtPrefixFormData, setGtPrefixFormData] = useState({
    cc_ndc: '',
    gt_node: '',
    mgt: ''
  });
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [formData, setFormData] = useState({
    tadig_code: '',
    country: '',
    country_iso2: '',
    operator_name: '',
    mcc_mnc: '',
    roaming_agreement_type: 'none',
    is_allowed: false,
    is_data_allowed: false,
  });

  useEffect(() => {
    fetchOperators();
  }, [page, search, filters]);

  // Loaded once: the list only changes when an operator is created or deleted.
  useEffect(() => {
    apiService
      .getOperatorCountries()
      .then(setCountries)
      .catch((error) => console.error('Error fetching countries:', error));
  }, []);

  // Any filter change restarts paging -- staying on page 7 of a narrower
  // result set shows an empty table and looks like the filter found nothing.
  const applyFilter = (patch: Partial<typeof filters>) => {
    setPage(1);
    setFilters({ ...filters, ...patch });
  };

  const fetchOperators = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        size: 20,
        search: search || undefined,
        country: filters.country || undefined,
        agreement_type: filters.agreement_type || undefined,
        is_allowed: filters.is_allowed ? filters.is_allowed === 'true' : undefined,
      };
      const response = await apiService.getOperators(params);
      setOperators(response.items);
      setTotalPages(response.pages);
    } catch (error) {
      console.error('Error fetching operators:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAllowed = async (operatorId: number, currentStatus: boolean) => {
    try {
      await apiService.updateOperator(operatorId, { is_allowed: !currentStatus });
      fetchOperators();
    } catch (error) {
      console.error('Error updating operator:', error);
    }
  };

  const handleToggleDataAllowed = async (operatorId: number, currentStatus: boolean) => {
    try {
      await apiService.updateOperator(operatorId, { is_data_allowed: !currentStatus });
      fetchOperators();
    } catch (error) {
      console.error('Error updating operator:', error);
    }
  };

  const toggleRow = async (operatorId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(operatorId)) {
      newExpanded.delete(operatorId);
    } else {
      newExpanded.add(operatorId);
      // Load GT prefixes if not already loaded
      if (!gtPrefixes[operatorId]) {
        await loadGtPrefixes(operatorId);
      }
    }
    setExpandedRows(newExpanded);
  };

  const loadGtPrefixes = async (operatorId: number) => {
    try {
      setLoadingPrefixes(prev => new Set(prev).add(operatorId));
      const prefixes = await apiService.getGtPrefixes(operatorId);
      setGtPrefixes(prev => ({ ...prev, [operatorId]: prefixes }));
    } catch (error) {
      console.error('Error loading GT prefixes:', error);
    } finally {
      setLoadingPrefixes(prev => {
        const newSet = new Set(prev);
        newSet.delete(operatorId);
        return newSet;
      });
    }
  };

  const handleAddOperator = () => {
    setEditingOperator(null);
    setFormData({
      tadig_code: '',
      country: '',
      country_iso2: '',
      operator_name: '',
      mcc_mnc: '',
      roaming_agreement_type: 'none',
      is_allowed: false,
      is_data_allowed: false,
    });
    setIsModalOpen(true);
  };

  const handleEditOperator = (operator: any) => {
    setEditingOperator(operator);
    setFormData({
      tadig_code: operator.tadig_code,
      country: operator.country,
      country_iso2: operator.country_iso2 || '',
      operator_name: operator.operator_name,
      mcc_mnc: operator.mcc_mnc,
      roaming_agreement_type: operator.roaming_agreement_type || 'none',
      is_allowed: operator.is_allowed,
      is_data_allowed: operator.is_data_allowed,
    });
    setIsModalOpen(true);
  };

  const handleSaveOperator = async () => {
    try {
      if (editingOperator) {
        await apiService.updateOperator(editingOperator.id, formData);
      } else {
        await apiService.createOperator(formData);
      }
      setIsModalOpen(false);
      fetchOperators();
    } catch (error) {
      console.error('Error saving operator:', error);
    }
  };

  const handleAddGtPrefix = (operatorId: number) => {
    setCurrentOperatorId(operatorId);
    setEditingGtPrefix(null);
    setGtPrefixFormData({
      cc_ndc: '',
      gt_node: '',
      mgt: ''
    });
    setIsGtPrefixModalOpen(true);
  };

  const handleEditGtPrefix = (operatorId: number, prefix: GtPrefix) => {
    setCurrentOperatorId(operatorId);
    setEditingGtPrefix(prefix);
    setGtPrefixFormData({
      cc_ndc: prefix.cc_ndc,
      gt_node: prefix.gt_node || '',
      mgt: prefix.mgt || ''
    });
    setIsGtPrefixModalOpen(true);
  };

  const handleDeleteGtPrefix = async (operatorId: number, prefixId: number) => {
    if (!window.confirm('Are you sure you want to delete this GT prefix?')) {
      return;
    }
    try {
      await apiService.deleteGtPrefix(prefixId);
      await loadGtPrefixes(operatorId);
      fetchOperators(); // To update the count
    } catch (error) {
      console.error('Error deleting GT prefix:', error);
    }
  };

  const handleSaveGtPrefix = async () => {
    if (!currentOperatorId) return;

    try {
      if (editingGtPrefix) {
        await apiService.updateGtPrefix(editingGtPrefix.id, gtPrefixFormData);
      } else {
        await apiService.addGtPrefix(currentOperatorId, gtPrefixFormData);
      }
      setIsGtPrefixModalOpen(false);
      await loadGtPrefixes(currentOperatorId);
      fetchOperators(); // To update the count
    } catch (error) {
      console.error('Error saving GT prefix:', error);
    }
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Operators</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage roaming partners, their GT prefixes and the default template.
          </p>
          <div className="mt-3 rounded-md border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            The two switches below are a <strong>template</strong>, not a live decision. Steering
            answers from each brand&rsquo;s own list &mdash; change what a brand permits on{' '}
            <Link to="/brand-networks" className="font-medium underline">
              Brand Networks
            </Link>
            . The template only seeds a brand&rsquo;s list when it is first copied.
          </div>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={handleAddOperator}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Add operator
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div>
          <label htmlFor="search" className="sr-only">
            Search
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="search"
              id="search"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search TADIG, operator, MCC+MNC..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <select
          value={filters.country}
          onChange={(e) => applyFilter({ country: e.target.value })}
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        >
          <option value="">All Countries ({countries.length})</option>
          {countries.map((c) => (
            <option key={c.country} value={c.country}>
              {c.country} ({c.operator_count})
            </option>
          ))}
        </select>

        <select
          value={filters.agreement_type}
          onChange={(e) => applyFilter({ agreement_type: e.target.value })}
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        >
          <option value="">All Agreement Types</option>
          <option value="direct">Direct</option>
          <option value="k2r_comfone">K2R Comfone</option>
          <option value="bics_rhub">BICS RHub</option>
          <option value="none">None</option>
        </select>

        <select
          value={filters.is_allowed}
          onChange={(e) => applyFilter({ is_allowed: e.target.value })}
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        >
          <option value="">All Status</option>
          <option value="true">Allowed</option>
          <option value="false">Denied</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      TADIG
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Country
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Operator
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      MCC+MNC
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Agreement Type
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      GT Prefixes
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Template: roaming
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Template: data (3G)
                    </th>
                    <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : operators.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        No operators found
                      </td>
                    </tr>
                  ) : (
                    operators.map((operator) => (
                      <Fragment key={operator.id}>
                        <tr>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                            <div className="flex items-center">
                              <button
                                onClick={() => toggleRow(operator.id)}
                                className="mr-2 p-1 hover:bg-gray-100 rounded"
                              >
                                {expandedRows.has(operator.id) ? (
                                  <ChevronDownIcon className="h-4 w-4" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4" />
                                )}
                              </button>
                              {operator.tadig_code}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {operator.country}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">
                            {operator.operator_name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {operator.mcc_mnc}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {operator.roaming_agreement_type || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <button
                              onClick={() => toggleRow(operator.id)}
                              className="text-blue-600 hover:text-blue-900 underline"
                            >
                              {operator.gt_prefix_count || 0}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <button
                              onClick={() => handleToggleAllowed(operator.id, operator.is_allowed)}
                              title="Template only — does not change any live steering decision"
                              className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                                operator.is_allowed
                                  ? 'text-green-700 bg-green-100 hover:bg-green-200'
                                  : 'text-red-700 bg-red-100 hover:bg-red-200'
                              }`}
                            >
                              {operator.is_allowed ? (
                                <>
                                  <CheckIcon className="mr-1 h-3 w-3" />
                                  Allowed
                                </>
                              ) : (
                                <>
                                  <XIcon className="mr-1 h-3 w-3" />
                                  Denied
                                </>
                              )}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <button
                              onClick={() => handleToggleDataAllowed(operator.id, operator.is_data_allowed)}
                              title="Template only — does not change any live steering decision"
                              className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                                operator.is_data_allowed
                                  ? 'text-green-700 bg-green-100 hover:bg-green-200'
                                  : 'text-red-700 bg-red-100 hover:bg-red-200'
                              }`}
                            >
                              {operator.is_data_allowed ? (
                                <>
                                  <CheckIcon className="mr-1 h-3 w-3" />
                                  Data
                                </>
                              ) : (
                                <>
                                  <XIcon className="mr-1 h-3 w-3" />
                                  No data
                                </>
                              )}
                            </button>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button 
                              onClick={() => handleEditOperator(operator)}
                              className="text-indigo-600 hover:text-indigo-900">
                              Edit
                            </button>
                          </td>
                        </tr>
                        {expandedRows.has(operator.id) && (
                          <tr>
                            <td colSpan={9} className="px-6 py-4 bg-gray-50">
                              <div className="mb-4 flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-900">GT Prefixes</h4>
                                <button
                                  onClick={() => handleAddGtPrefix(operator.id)}
                                  className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  <PlusIcon className="mr-1 h-3 w-3" />
                                  Add GT Prefix
                                </button>
                              </div>
                              {loadingPrefixes.has(operator.id) ? (
                                <div className="text-center py-2">Loading GT prefixes...</div>
                              ) : gtPrefixes[operator.id]?.length > 0 ? (
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead>
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        CC+NDC
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        GT Node
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        MGT
                                      </th>
                                      <th className="relative px-3 py-2">
                                        <span className="sr-only">Actions</span>
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 bg-white">
                                    {gtPrefixes[operator.id].map((prefix) => (
                                      <tr key={prefix.id}>
                                        <td className="px-3 py-2 text-sm text-gray-900">
                                          {prefix.cc_ndc}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500">
                                          {prefix.gt_node || '-'}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-500">
                                          {prefix.mgt || '-'}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-right space-x-2">
                                          <button
                                            onClick={() => handleEditGtPrefix(operator.id, prefix)}
                                            className="text-indigo-600 hover:text-indigo-900"
                                          >
                                            <PencilIcon className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteGtPrefix(operator.id, prefix.id)}
                                            className="text-red-600 hover:text-red-900"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="text-sm text-gray-500 text-center py-2">
                                  No GT prefixes configured
                                </p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Transition.Root show={isModalOpen} as={Fragment}>
        <Dialog as="div" open={isModalOpen} className="fixed z-10 inset-0 overflow-y-auto" onClose={setIsModalOpen}>
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {editingOperator ? 'Edit Operator' : 'Add New Operator'}
                  </h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label htmlFor="tadig_code" className="block text-sm font-medium text-gray-700">
                        TADIG Code
                      </label>
                      <input
                        type="text"
                        id="tadig_code"
                        value={formData.tadig_code}
                        onChange={(e) => setFormData({ ...formData, tadig_code: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="operator_name" className="block text-sm font-medium text-gray-700">
                        Operator Name
                      </label>
                      <input
                        type="text"
                        id="operator_name"
                        value={formData.operator_name}
                        onChange={(e) => setFormData({ ...formData, operator_name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                        Country
                      </label>
                      <input
                        type="text"
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="country_iso2" className="block text-sm font-medium text-gray-700">
                        Country ISO2 Code
                      </label>
                      <input
                        type="text"
                        id="country_iso2"
                        value={formData.country_iso2}
                        onChange={(e) => setFormData({ ...formData, country_iso2: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="e.g. US, GB, FR"
                      />
                    </div>
                    <div>
                      <label htmlFor="mcc_mnc" className="block text-sm font-medium text-gray-700">
                        MCC+MNC
                      </label>
                      <input
                        type="text"
                        id="mcc_mnc"
                        value={formData.mcc_mnc}
                        onChange={(e) => setFormData({ ...formData, mcc_mnc: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="roaming_agreement_type" className="block text-sm font-medium text-gray-700">
                        Agreement Type
                      </label>
                      <select
                        id="roaming_agreement_type"
                        value={formData.roaming_agreement_type}
                        onChange={(e) => setFormData({ ...formData, roaming_agreement_type: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="direct">Direct</option>
                        <option value="k2r_comfone">K2R Comfone</option>
                        <option value="bics_rhub">BICS RHub</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_allowed"
                        checked={formData.is_allowed}
                        onChange={(e) => setFormData({ ...formData, is_allowed: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_allowed" className="ml-2 block text-sm text-gray-900">
                        Allow roaming (template)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_data_allowed"
                        checked={formData.is_data_allowed}
                        onChange={(e) => setFormData({ ...formData, is_data_allowed: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_data_allowed" className="ml-2 block text-sm text-gray-900">
                        Allow data roaming, 3G (template)
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="button"
                    onClick={handleSaveOperator}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                  >
                    {editingOperator ? 'Save' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* GT Prefix Modal */}
      <Transition.Root show={isGtPrefixModalOpen} as={Fragment}>
        <Dialog as="div" open={isGtPrefixModalOpen} className="fixed z-10 inset-0 overflow-y-auto" onClose={setIsGtPrefixModalOpen}>
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                  {editingGtPrefix ? 'Edit GT Prefix' : 'Add GT Prefix'}
                </Dialog.Title>
                <div className="mt-5">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="cc_ndc" className="block text-sm font-medium text-gray-700">
                        CC+NDC <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="cc_ndc"
                        value={gtPrefixFormData.cc_ndc}
                        onChange={(e) => setGtPrefixFormData({ ...gtPrefixFormData, cc_ndc: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="e.g., 38160"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="gt_node" className="block text-sm font-medium text-gray-700">
                        GT Node
                      </label>
                      <input
                        type="text"
                        id="gt_node"
                        value={gtPrefixFormData.gt_node}
                        onChange={(e) => setGtPrefixFormData({ ...gtPrefixFormData, gt_node: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label htmlFor="mgt" className="block text-sm font-medium text-gray-700">
                        MGT
                      </label>
                      <input
                        type="text"
                        id="mgt"
                        value={gtPrefixFormData.mgt}
                        onChange={(e) => setGtPrefixFormData({ ...gtPrefixFormData, mgt: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="button"
                    onClick={handleSaveGtPrefix}
                    disabled={!gtPrefixFormData.cc_ndc}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingGtPrefix ? 'Save' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsGtPrefixModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default Operators;